import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { endOfDay, startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormDateInput from '../../../components/form/FormDateInput';
import AdminHeader from '../../../components/header/AdminHeader';
import CallexLoader from '../../../components/CallexLoader';

import {
  archiveOptionsGet,
  performanceDispositionReportExport,
  performanceDispositionReportGet,
  processAllOptionGet,
  userOptionGetByProcess
} from '../../../store/slices/tenantSlices/dailyReportSlice';
import AgentDispositionPreview from './AgentDispositionPreview';
import FormSelect from '../../../components/form/FormSelect';
import {
  checkModulePermission,
  formatArchiveOptions,
  formatPayloadDate,
  getDateRangeFromLabel
} from '../../../utils/common';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import { useTimezone } from '../../../store/slices/commonSlice';
import { toZonedTime } from 'date-fns-tz';

const AgentDispositionReport = () => {
  const dispatch = useDispatch();
  const timezone = useTimezone();
  const userRolePermission = useUserRolePermission();

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [processOptions, setProcessOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [archiveOptions, setArchiveOptions] = useState([]);
  const [exportDetails, setExportDetails] = useState(null);

  const today = toZonedTime(new Date(), timezone);

  const initialValues = {
    start_date: startOfDay(today),
    end_date: endOfDay(today),
    processIds: [],
    userIds: [],
    details: 'login user',
    archiveId: 0
  };

  const validationSchema = Yup.object({
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date().required('End date is required'),
    processIds: Yup.array().min(1, 'Process is required').required('Process is required'),
    userIds: Yup.array().min(1, 'User is required').required('User is required')
    // details: Yup.array().min(1, 'Details is required').required('Details is required')
  });

  const onSubmit = async () => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);
      const serializedProcessId = `[${[values?.processIds].join(',')}]`;
      const serializedUserIds = `[${[values?.userIds].join(',')}]`;

      const payload = {
        ...values,
        processIds: serializedProcessId,
        userIds: serializedUserIds
      };
      payload['start_date'] = formattedStartDate;
      payload['end_date'] = formattedEndDate;

      const response = await dispatch(performanceDispositionReportGet(payload)).unwrap();
      if (response) {
        setReportData(response);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const { values, handleBlur, setFieldValue, handleSubmit, handleChange, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  const getProcessOption = async () => {
    try {
      const response = await dispatch(processAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  const getUserOption = async () => {
    try {
      const serializedProcessId = `[${[values?.processIds].join(',')}]`;
      const response = await dispatch(
        userOptionGetByProcess({ processIds: serializedProcessId })
      ).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.Username || '-',
          value: val.id
        }));
        setUserOptions(options);
      } else {
        setUserOptions([]);
      }
    } catch (error) {
      setUserOptions([]);
      console.log('error', error);
    }
  };

  const getArchiveOptions = async () => {
    try {
      const response = await dispatch(archiveOptionsGet()).unwrap();
      if (response && response.data) {
        const options = formatArchiveOptions(response.data, timezone);
        setArchiveOptions(options);
      } else {
        setArchiveOptions([]);
      }
    } catch (error) {
      setArchiveOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getArchiveOptions();
    getProcessOption();
  }, []);

  useEffect(() => {
    if (values?.processIds?.length > 0) {
      getUserOption();
    } else {
      setUserOptions([]);
    }
  }, [values?.processIds]);

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);
      const serializedProcessId = `[${[values?.processIds].join(',')}]`;
      const serializedUserIds = `[${[values?.userIds].join(',')}]`;
      const serializedDetails = `[${[values?.details].join(',')}]`;

      const payload = {
        ...values,
        processIds: serializedProcessId,
        details: serializedDetails,
        userIds: serializedUserIds,
        archiveId: values?.archiveId
      };
      payload['start_date'] = formattedStartDate;
      payload['end_date'] = formattedEndDate;
      const response = await dispatch(performanceDispositionReportExport(payload)).unwrap();
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.download;
      link.click();
      window.URL.revokeObjectURL(url);
      setIsExporting(false);
    } catch (error) {
      setIsExporting(false);
      console.log('Error exporting CSV:', error);
    }
  };

  // Only the numeric count columns drill down - the agent identity columns hold strings,
  // so a positive number is enough to tell a count cell apart without hardcoding column names.
  const isDrillDownCell = (val, header) => Number(val?.[header?.name]) > 0;

  // Cells under a disposition section filter the details by that disposition,
  // the "Total Calls" column pulls every disposition for the row.
  const getDrillDownDisposition = (section, header) =>
    /total/i.test(section) ? undefined : header?.name;

  // A row carries its own process/user, so drill down into just that pair.
  // Only the total rows fall back to everything picked in the filter.
  const handleDispositionDetails = (section, header, val, isTotal, isGrandTotal) => {
    const serializeArray = (value) => `[${[value].join(',')}]`;
    const isAggregateRow = isTotal || isGrandTotal;

    setExportDetails({
      start_date: formatPayloadDate(values.start_date),
      end_date: formatPayloadDate(values.end_date),
      processIds: serializeArray(
        !isAggregateRow && val?.process_id ? [val?.process_id] : values?.processIds
      ),
      userIds: serializeArray(!isAggregateRow && val?.user_id ? [val?.user_id] : values?.userIds),
      disposition: getDrillDownDisposition(section, header),
      archiveId: values?.archiveId
    });
  };

  const selectedOption = useMemo(
    () => archiveOptions.find((opt) => opt.value === values.archiveId),
    [values.archiveId, archiveOptions]
  );

  const selectedLabel = selectedOption?.label || null;

  const { minDate, maxDate } = useMemo(() => {
    return getDateRangeFromLabel(selectedLabel, timezone);
  }, [selectedLabel, timezone]);

  useEffect(() => {
    if (minDate && maxDate) {
      setFieldValue('start_date', minDate);
      setFieldValue('end_date', maxDate);
    }
  }, [minDate?.getTime(), maxDate?.getTime()]);

  return (
    <div>
      <AdminHeader
        title="Agent Disposition Report"
        isSearch={true}
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isRefresh={reportData?.data?.length > 0}
        onRefreshClick={() => onSubmit()}
        isManualExport
        isExporting={isExporting}
        isExportDisabled={
          !checkModulePermission('is_export', 'Agent Disposition Report', userRolePermission)
        }
        // isExport={reportData.data?.length > 0}
        onExportClick={() => handleExportCSV()}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Month"
                  placeholder="Select month"
                  value={values.archiveId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="archiveId"
                  options={archiveOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormDateInput
                  name="start_date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="From Date"
                  showTimeSelect={true}
                  minDate={minDate}
                  maxDate={values.end_date || maxDate}
                  value={values.start_date}
                  onChange={(e) => setFieldValue('start_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormDateInput
                  name="end_date"
                  label="To Date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  minDate={values.start_date || minDate}
                  maxDate={maxDate}
                  value={values.end_date}
                  onChange={(e) => setFieldValue('end_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Process"
                  placeholder="Select Process"
                  value={values.processIds}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="processIds"
                  options={processOptions}
                  touched={touched}
                  errors={errors}
                  isMulti={true}
                />
              </Col>
              <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="User"
                  placeholder="Select User"
                  value={values.userIds}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="userIds"
                  options={userOptions}
                  touched={touched}
                  errors={errors}
                  isMulti={true}
                />
              </Col>
              {/* <Col lg="2" md="2" className="flex-row">
                <FormSelect
                  label="Details"
                  placeholder="Select details"
                  value={values.details}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="details"
                  options={performanceDetailsOptions}
                  touched={touched}
                  errors={errors}
                  isMulti={true}
                />
              </Col> */}
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData.data?.length > 0 ? (
        <Card className="p-0 position-relative mb-0">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  {/* Render the sections of headers as titles */}
                  {Object.keys(reportData?.header).map((section, index) => (
                    <th key={index} colSpan={reportData?.header[section].length}>
                      {section}
                    </th>
                  ))}
                </tr>
                <tr>
                  {/* Render the labels for each section as table headers */}
                  {Object.keys(reportData?.header).map((section, index) =>
                    reportData?.header[section].map((header, idx) => (
                      <th key={idx}>{header.label}</th>
                    ))
                  )}
                </tr>
              </thead>
              <tbody>
                {reportData?.data?.map((val, index) => {
                  const isGrandTotal = Object.values(val).includes('Grand Total');
                  const isTotal = Object.values(val).includes('Total');
                  return (
                    <tr
                      key={index}
                      style={
                        isTotal
                          ? { backgroundColor: '#12233533' }
                          : isGrandTotal
                            ? { backgroundColor: '#2cd07e33' }
                            : {}
                      }>
                      {Object.keys(reportData?.header).map((section, sectionIdx) =>
                        reportData?.header[section].map((header, labelIdx) => {
                          const isClickable = isDrillDownCell(val, header);
                          return (
                            <td
                              key={`${sectionIdx}-${labelIdx}`}
                              style={{
                                textDecoration: isClickable ? 'underline' : 'unset',
                                cursor: isClickable ? 'pointer' : 'unset'
                              }}
                              onClick={() => {
                                if (isClickable) {
                                  handleDispositionDetails(
                                    section,
                                    header,
                                    val,
                                    isTotal,
                                    isGrandTotal
                                  );
                                }
                              }}>
                              {val[header.name] || '-'}
                            </td>
                          );
                        })
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          No data available
        </Card>
      )}
      {exportDetails && (
        <AgentDispositionPreview
          isOpen={exportDetails}
          exportDetails={exportDetails}
          onCloseModal={() => setExportDetails(null)}
        />
      )}
    </div>
  );
};

export default AgentDispositionReport;
